import { Component, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Customer } from '../../models/customer';
import { StandardStatisticsData, OfferStatisticsData } from '../../models/statistics';
import { CustomerService } from '../../services/customers/customer.service';
import { PriceConfigService } from '../../services/price-config/price-config.service';
import { OrderOfferStatisticsService } from '../../services/order-offer-statistics/order-offer-statistics.service';

@Component({
  selector: 'lib-order-offer-data-modal',
  templateUrl: './order-offer-data-modal.component.html',
  styleUrls: ['./order-offer-data-modal.component.scss'],
  standalone: false
})
export class OrderOfferDataModalComponent implements OnInit {
  @Input() productId: string;
  @Input() movable = false;

  activeModal = inject(NgbActiveModal);
  protected router = inject(Router);
  private statisticsService = inject(OrderOfferStatisticsService);
  private priceConfigService = inject(PriceConfigService);
  protected customerService = inject(CustomerService);

  loading = true;
  productName: string;
  orders: StandardStatisticsData[] = [];
  offers: OfferStatisticsData[] = [];
  activeNavId: string = 'orders';

  orderSearch = '';
  orderDateFrom = '';
  orderDateTo = '';
  orderSortCol: string | null = null;
  orderSortDir: 'asc' | 'desc' = 'asc';

  offerSearch = '';
  offerDateFrom = '';
  offerDateTo = '';
  offerSortCol: string | null = null;
  offerSortDir: 'asc' | 'desc' = 'asc';

  orderSumQty = 0;
  orderSumPrice = 0;
  orderSumCurrency = '';

  offerSumQty = 0;
  offerSumPrice = 0;
  offerSumCurrency = '';

  private allOrders: StandardStatisticsData[] = [];
  private allOffers: OfferStatisticsData[] = [];
  protected customerMap: { [customerId: string]: Customer } = {};

  filterOrders(term: string): void {
    this.orderSearch = term;
    this.buildOrdersView();
  }

  filterOrdersByDate(from: string, to: string): void {
    this.orderDateFrom = from;
    this.orderDateTo = to;
    this.buildOrdersView();
  }

  sortOrders(col: string): void {
    if (this.orderSortCol === col) {
      this.orderSortDir = this.orderSortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.orderSortCol = col;
      this.orderSortDir = 'asc';
    }
    this.buildOrdersView();
  }

  private buildOrdersView(): void {
    const t = this.orderSearch.toLowerCase();
    let result = t
      ? this.allOrders.filter(r => r.customerId?.toLowerCase().includes(t) || r.customerName?.toLowerCase().includes(t))
      : [...this.allOrders];

    if (this.orderDateFrom || this.orderDateTo) {
      result = result.filter(r => {
        const d = r.orderCreationDate?.substring(0, 10) ?? '';
        return (!this.orderDateFrom || d >= this.orderDateFrom) && (!this.orderDateTo || d <= this.orderDateTo);
      });
    }

    if (this.orderSortCol) {
      const dir = this.orderSortDir === 'asc' ? 1 : -1;
      const col = this.orderSortCol;
      result = result.sort((a, b) => {
        const av = this.getOrderSortValue(a, col);
        const bv = this.getOrderSortValue(b, col);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return av < bv ? -dir : av > bv ? dir : 0;
      });
    }

    this.orders = result;
    this.orderSumQty = result.reduce((sum, r) => sum + (r.orderRowTotalQuantity ?? 0), 0);
    this.orderSumPrice = result.reduce((sum, r) => sum + (r.orderRowTotalValueCurrency ?? 0), 0);
    this.orderSumCurrency = this.getCommonCurrency(result);
  }

  private getOrderSortValue(row: StandardStatisticsData, col: string): number | string | null {
    switch (col) {
      case 'qty': return row.orderRowTotalQuantity ?? null;
      case 'date': return row.orderCreationDate ?? null;
      case 'unitPrice': return row.orderRowTotalQuantity ? row.orderRowTotalValueCurrency / row.orderRowTotalQuantity : null;
      case 'price': return row.orderRowTotalValueCurrency ?? null;
      default: return null;
    }
  }

  filterOffers(term: string): void {
    this.offerSearch = term;
    this.buildOffersView();
  }

  filterOffersByDate(from: string, to: string): void {
    this.offerDateFrom = from;
    this.offerDateTo = to;
    this.buildOffersView();
  }

  sortOffers(col: string): void {
    if (this.offerSortCol === col) {
      this.offerSortDir = this.offerSortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.offerSortCol = col;
      this.offerSortDir = 'asc';
    }
    this.buildOffersView();
  }

  private buildOffersView(): void {
    const t = this.offerSearch.toLowerCase();
    let result = t
      ? this.allOffers.filter(r => r.customerId?.toLowerCase().includes(t) || r.customerName?.toLowerCase().includes(t))
      : [...this.allOffers];

    if (this.offerDateFrom || this.offerDateTo) {
      result = result.filter(r => {
        const d = r.offerCreationDate?.substring(0, 10) ?? '';
        return (!this.offerDateFrom || d >= this.offerDateFrom) && (!this.offerDateTo || d <= this.offerDateTo);
      });
    }

    if (this.offerSortCol) {
      const dir = this.offerSortDir === 'asc' ? 1 : -1;
      const col = this.offerSortCol;
      result = result.sort((a, b) => {
        const av = this.getOfferSortValue(a, col);
        const bv = this.getOfferSortValue(b, col);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return av < bv ? -dir : av > bv ? dir : 0;
      });
    }

    this.offers = result;
    this.offerSumQty = result.reduce((sum, r) => sum + (r.offerRowTotalQuantity ?? 0), 0);
    this.offerSumPrice = result.reduce((sum, r) => sum + (r.offerRowTotalValueCurrency ?? 0), 0);
    this.offerSumCurrency = this.getCommonCurrency(result);
  }

  private getOfferSortValue(row: OfferStatisticsData, col: string): number | string | null {
    switch (col) {
      case 'qty': return row.offerRowTotalQuantity ?? null;
      case 'date': return row.offerCreationDate ?? null;
      case 'unitPrice': return row.offerRowTotalQuantity ? row.offerRowTotalValueCurrency / row.offerRowTotalQuantity : null;
      case 'price': return row.offerRowTotalValueCurrency ?? null;
      default: return null;
    }
  }

  getCurrencyCode(row: StandardStatisticsData | OfferStatisticsData): string {
    const customer = this.customerMap[row.customerId];
    return (
      (row as any).currency ||
      customer?.extra?.gme?.currencyCode ||
      customer?.extra?.customers?.currencyCode ||
      customer?.extra?.gungCustomers?.currencyCode ||
      this.priceConfigService.getCurrency()
    );
  }

  private getCommonCurrency(rows: (StandardStatisticsData | OfferStatisticsData)[]): string {
    if (rows.length === 0) {
      return '';
    }
    const first = this.getCurrencyCode(rows[0]);
    return rows.every(r => this.getCurrencyCode(r) === first) ? first : '';
  }

  openOrderDetail(orderId: string): void {
    window.open(this.router.serializeUrl(this.router.createUrlTree(['/orders', orderId])), '_blank');
  }

  openOfferDetail(offerId: string): void {
    window.open(this.router.serializeUrl(this.router.createUrlTree(['/offers', offerId])), '_blank');
  }

  ngOnInit(): void {
    forkJoin({
      orders: this.statisticsService.getOrderStatistics(this.productId),
      offers: this.statisticsService.getOfferStatistics(this.productId)
    }).pipe(
      switchMap(({ orders, offers }) => {
        const uniqueIds = [...new Set([...orders, ...offers].map(r => r.customerId).filter(Boolean))];
        const customerRequests = uniqueIds.length
          ? forkJoin(uniqueIds.map(id => this.customerService.getCustomer(id)))
          : of([]);
        return customerRequests.pipe(
          switchMap(customers => of({ orders, offers, customers }))
        );
      })
    ).subscribe({
      next: ({ orders, offers, customers }) => {
        customers.forEach(c => (this.customerMap[c.id] = c));
        this.productName = orders[0]?.productName ?? offers[0]?.productName;
        this.allOrders = orders.sort((a, b) => (b.orderCreationDate ?? '').localeCompare(a.orderCreationDate ?? ''));
        this.allOffers = offers.sort((a, b) => (b.offerCreationDate ?? '').localeCompare(a.offerCreationDate ?? ''));
        this.buildOrdersView();
        this.buildOffersView();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }
}
